import React, { useEffect, useState } from 'react';
import { PortalApplicationsService } from '../../services/portal/portalApplicationsService';
import { useClientPortal } from '../../context/ClientPortalContext';
import { Link } from 'react-router-dom';
import UniversityCard from '../../components/client/applications/UniversityCard';
import { AcademicCapIcon, GlobeAmericasIcon, BriefcaseIcon } from '@heroicons/react/24/outline';

const VisaCard = ({ app }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
    <div className="flex items-center gap-3 mb-4">
      <div className="h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover:bg-emerald-100 transition-colors">
        <GlobeAmericasIcon className="h-6 w-6" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-slate-900 truncate">{app.country}</h3>
        <p className="text-sm text-slate-500">{app.visa_type}</p>
      </div>
      <span className="px-2 py-1 text-xs font-medium rounded-full capitalize bg-emerald-100 text-emerald-700 border border-emerald-200">
        {app.status}
      </span>
    </div>

    {/* Progress */}
    <div className="mb-4">
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>Status</span>
        <span className="capitalize">{app.stage || 'In Progress'}</span>
      </div>
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="bg-gradient-to-r from-emerald-500 to-teal-500 h-full rounded-full transition-all duration-500"
          style={{ width: app.stage === 'completed' ? '100%' : '50%' }}
        ></div>
      </div>
    </div>

    {/* Details */}
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-400">
        {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'Not submitted'}
      </span>
      <span className="text-emerald-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
        View details →
      </span>
    </div>
  </div>
);

const CaseCard = ({ item }) => (
  <Link
    to={`/portal/cases/${item.id}`}
    className="block bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-100 transition-colors">
        <BriefcaseIcon className="h-6 w-6" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-slate-900 truncate">{item.title}</h3>
        <p className="text-sm text-slate-500 truncate">{item.description || 'No description'}</p>
      </div>
      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize border ${item.status === 'active' ? 'bg-blue-100 text-blue-700 border-blue-200' :
        item.status === 'completed' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
          'bg-slate-100 text-slate-700 border-slate-200'
        }`}>
        {item.status}
      </span>
    </div>

    <div className="flex items-center justify-between text-sm mt-4">
      <span className="text-slate-400">
        Created: {new Date(item.created_at).toLocaleDateString()}
      </span>
      {item.assigned_to && (
        <span className="text-slate-500 text-xs bg-slate-50 px-2 py-1 rounded">
          Agent: {item.assigned_to}
        </span>
      )}
    </div>
  </Link>
);

const EmptyState = ({ type }) => (
  <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
      {type === 'university' ? (
        <AcademicCapIcon className="h-8 w-8 text-slate-400" />
      ) : type === 'visa' ? (
        <GlobeAmericasIcon className="h-8 w-8 text-slate-400" />
      ) : (
        <BriefcaseIcon className="h-8 w-8 text-slate-400" />
      )}
    </div>
    <h3 className="text-lg font-medium text-slate-700 mb-1">
      No {type === 'university' ? 'university' : type === 'visa' ? 'visa' : 'case'} {type === 'case' ? 'records' : 'applications'} yet
    </h3>
    <p className="text-sm text-slate-500 max-w-sm mx-auto">
      {type === 'university'
        ? "Your university applications will appear here once they're created."
        : type === 'visa'
          ? "Your visa applications will appear here once they're initiated."
          : "Your active cases will appear here."}
    </p>
  </div>
);

const ClientApplicationsPage = () => {
  const { clientId } = useClientPortal();
  const [visaApps, setVisaApps] = useState([]);
  const [uniApps, setUniApps] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (clientId) {
      loadApps();
    }
  }, [clientId]);

  const loadApps = async () => {
    setLoading(true);
    const [visa, uni, clientCases] = await Promise.all([
      PortalApplicationsService.listVisaApplications(clientId),
      PortalApplicationsService.listUniversityApplications(clientId),
      PortalApplicationsService.listCases(clientId)
    ]);
    setVisaApps(visa.data || []);
    setUniApps(uni.data || []);
    setCases(clientCases.data || []);
    setLoading(false);
  };

  const tabs = [
    { id: 'all', label: 'All Items', count: uniApps.length + visaApps.length + cases.length },
    { id: 'cases', label: 'Cases', count: cases.length },
    { id: 'university', label: 'University', count: uniApps.length },
    { id: 'visa', label: 'Visa', count: visaApps.length }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-slate-100 rounded-xl animate-pulse"></div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-48 bg-slate-100 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with tabs */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-900">My Applications & Cases</h1>
            <p className="text-sm text-slate-500 mt-0.5">Track your cases, university and visa applications</p>
          </div>
          <button
            onClick={loadApps}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-4 border-t border-slate-100 pt-4 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                                flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap
                                ${activeTab === tab.id
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-slate-600 hover:bg-slate-50'}
                            `}
            >
              {tab.label}
              <span className={`
                                px-1.5 py-0.5 rounded-full text-xs
                                ${activeTab === tab.id ? 'bg-indigo-100' : 'bg-slate-100'}
                            `}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Cases Section */}
      {(activeTab === 'all' || activeTab === 'cases') && (
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BriefcaseIcon className="h-5 w-5 text-blue-500" />
            Cases
          </h2>
          {cases.length === 0 ? (
            <EmptyState type="case" />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cases.map(item => (
                <CaseCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* University Applications */}
      {(activeTab === 'all' || activeTab === 'university') && (
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <AcademicCapIcon className="h-5 w-5 text-indigo-500" />
            University Applications
          </h2>
          {uniApps.length === 0 ? (
            <EmptyState type="university" />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {uniApps.map(app => (
                <UniversityCard key={app.id} application={app} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Visa Applications */}
      {(activeTab === 'all' || activeTab === 'visa') && (
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <GlobeAmericasIcon className="h-5 w-5 text-emerald-500" />
            Visa Applications
          </h2>
          {visaApps.length === 0 ? (
            <EmptyState type="visa" />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {visaApps.map(app => (
                <VisaCard key={app.id} app={app} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default ClientApplicationsPage;
